<div class="contentInner">
  <h1 class="sectionHeader">Connecting Dash to a Microsoft 365 business or personal account</h1>
  <p class="intro">
    Connecting Dash via the method described below is recommended for small businesses.  If you are a larger business,
    or you would like to connect Dash without having to create an extra Microsoft 365 account then you can connect Dash via an
    <a routerLink="../o365-enterprise-application">Enterprise Application</a>.
  </p>
  <p>
    Once you have set up your meeting rooms, you will need to choose an Microsoft 365 user account to use with Dash (this
    can be either a new account or an existing one).  Your chosen account will need access to all the meeting room
    calendars that you want to use with Dash.  To do this, you need to assume the identity of your meeting room
    mailbox (by making it a <i>delegate</i> of the resource in the Exchange admin center) and then set up sharing
    permissions:
  </p>
  <ol class="instructionList">
    <li>
      <div>
        <p><b>Set up the delegate:</b><br/> Select your meeting room from the resource list in the
          <a href="https://admin.exchange.microsoft.com/#/resources" target="_blank">Exchange admin center</a>.<br/><br/>
            Select the <i>Delegation</i> tab and then click the <i>Edit</i> button in the <i>Read and manage (Full Access)</i>
          section.  Enter the name of the account you wish to use with Dash.  <br/><br/>Set the permissions to <i>Full
            Access</i> and click <i>Save</i>.</p>
        <app-illustration class="illustration officeSetDelegate" src="../../assets/images/office-set-delegate.png" webpSrc="../../assets/images/office-set-delegate.webp" width="170" height="232"></app-illustration>
      </div>
    </li>
    <li>
      <div>
        <p><b>Open the meeting room mailbox:</b><br/> Open Outlook on the web, logged in as the account you want to use
          with Dash. <br/><br/>Click your user avatar in the top right corner of the window and select <i>Open another
            mailbox</i>.<br/><br/>Enter the email address of your resource calendar and click <i>Open</i>.  The resource mailbox
          should open in a new browser window or tab.</p>
        <app-illustration class="illustration officeOpenMailbox" src="../../assets/images/office-open-mailbox.png" webpSrc="../../assets/images/office-open-mailbox.webp" width="170" height="241"></app-illustration>
      </div>
    </li>
    <li>
      <div>
        <p><b>Set sharing permissions:</b><br/> Make sure you are in the calendar view (not the email inbox) and click
          "Share" on the right-hand side of the toolbar.<br/><br/>  Enter the email address of the account you wish to use with Dash,
          and select "Can edit" as the permission.  Click <i>Share</i>.</p>
        <app-illustration class="illustration officeSharingPermissions" src="../../assets/images/office-sharing-permissions.png" webpSrc="../../assets/images/office-sharing-permissions.webp" width="170" height="204"></app-illustration>
      </div>
    </li>
    <li>
      <div>
        <p><b>Accept the share request:</b><br/> Switch back to your browser tab for the account you wish to use with
          Dash.<br/><br/>  You should see a new email from the resource titled "I'd like to share my calendar with you".  Open
          the message and click <i>Accept</i> then click <i>View Calendar</i>.<br/><br/>  The meeting room should now appear in
          Dash.</p>
        <app-illustration class="illustration officeAcceptShare" src="../../assets/images/office-accept-share.png" webpSrc="../../assets/images/office-accept-share.webp" width="170" height="244"></app-illustration>
      </div>
    </li>
  </ol>
  <p class="tip icon">Dash requires read-only access to the calendars in order to display the meeting room schedules,
    and if you want to use Dash's advanced features such as room booking and check-in then write access to these
    calendars is also required.
  </p>

  <p>&nbsp;</p>

  <h2>Connecting Dash to the account</h2>
  <p>
    Once you have completed the sharing steps above, you can connect your Microsoft 365 account to Dash.  Open
    Dash and go to Settings (the <i>cog</i> icon in the bottom right of the main display).  If this is your first time using Dash,
    you will be asked to choose a calendar service immediately.  If you have used Dash before you should see the name
    of the calendar service you are currently connected to, and a <i>Change</i> button.  Click <i>Change</i>.
  </p>
  <p>
    You should see a list of calendar services to choose from (Microsoft 365, Google, and your device calendar).  Select
    <b>Microsoft 365 or Microsoft Account</b> and then select "Personal or service account".  You will be asked to sign in
    to your Microsoft account - sign in using the account that you created for Dash.  You should then see a screen asking
    permission to grant Dash access to your account.  Dash will request the following permissions:
  </p>
  <ul>
    <li><b>View a list of your calendars - </b>This is necessary in order to allow you to choose the calendar(s) you want to use with Dash.</li>
    <li><b>Read and write access to your calendars - </b>This allows Dash to read meeting information from your
      calendars, and also allows you to use Dash's advanced features such as booking a room from the display and
      automatically cancelling abandoned meetings.</li>
  </ul>
  <p>
    Dash will not be given any further access to your Microsoft 365 account, and will only be able to read the information
    listed above. Once you have granted Dash permission to access your account, check out our <a routerLink="../quickStart">Quick start guide</a>
    for more information on how to get started with Dash.
  </p>
</div>
